import { toImgix } from './imgix';

export type BreakPoint = 360 | 576 | 768 | 992 | 1200 | 1440 | 1920;
export type Dpr = 0.5 | 1 | 1.5 | 2;
export type Modification = 'Gradient' | 'RightCornerMark';
export type PictureSourceSet = {
  dpr?: Dpr;
  url: string;
};
export type PictureSource = {
  sourceSet: PictureSourceSet[];
  maxPixelWidth: number;
};
export type Picture = {
  aspectRatio: string | null;
  url: string;
  sources: PictureSource[];
};

const defaultBreakpoints: BreakPoint[] = [360, 576, 768, 992, 1200, 1440, 1920];
const defaultDprs: Dpr[] = [0.5, 1, 1.5, 2];
const defaultModifications: Modification[] = [];

type CreatePictureType = {
  baseUrl: string | Partial<Record<BreakPoint, string>>;
  modifications?: Modification[];
  aspectRatio?: string | null;
  dprs?: Dpr[];
  breakPoints?: BreakPoint[];
  imgixExtraParams?: Record<string, string | string[]>;
};

export function createPicture({
  baseUrl,
  modifications = defaultModifications,
  aspectRatio = null,
  dprs = defaultDprs,
  breakPoints = defaultBreakpoints,
  imgixExtraParams = {},
}: CreatePictureType): Picture {
  const sources: PictureSource[] = breakPoints
    .map((breakpoint, i) => {
      const url = typeof baseUrl === 'string' ? baseUrl : baseUrl[breakpoint];
      if (!url) return undefined;
      return {
        sourceSet: dprs.map(dpr => ({
          dpr,
          url: toImgix(url, {
            gradient: modifications.includes('Gradient'),
            rightCornerMark: modifications.includes('RightCornerMark'),
            w: breakpoint,
            dpr,
            auto: ['format', 'compress'],
            fm: 'webp',
            ...imgixExtraParams,
          }),
        })),
        maxPixelWidth: i === breakPoints.length - 1 ? 0 : breakpoint,
      };
    })
    .filter(Boolean);

  return {
    url: sources[sources.length - 1].sourceSet[0].url,
    sources,
    aspectRatio,
  };
}

export function createStandardAndMobileTeaserPicture(
  standardImage: string,
  mobileImage: string | undefined,
  breakpoint: number,
  maxImageWidth: number,
  modifications: Modification[] = []
): Picture {
  const sources: PictureSource[] = [
    {
      maxPixelWidth: breakpoint,
      sourceSet: [
        {
          dpr: 1,
          url: toImgix(mobileImage ?? standardImage, {
            gradient: modifications.includes('Gradient'),
            rightCornerMark: modifications.includes('RightCornerMark'),
            w: breakpoint,
            auto: ['format', 'compress'],
          }),
        },
      ],
    },
    {
      maxPixelWidth: 0,
      sourceSet: [
        {
          dpr: 1,
          url: toImgix(standardImage, {
            gradient: modifications.includes('Gradient'),
            rightCornerMark: modifications.includes('RightCornerMark'),
            w: maxImageWidth,
            auto: ['format', 'compress'],
          }),
        },
      ],
    },
  ];

  return {
    url: sources[sources.length - 1].sourceSet[0].url,
    aspectRatio: null,
    sources,
  };
}

export function createStandardAndMobileTeaserPictureLandingpage(
  standardImage: string,
  mobileImage: string | undefined,
  breakpoint: number,
  maxImageWidth: number,
  modifications: Modification[] = []
): Picture {
  const sources: PictureSource[] = [
    {
      maxPixelWidth: breakpoint,
      sourceSet: [
        {
          dpr: 1,
          url: toImgix(mobileImage ?? standardImage, {
            gradient: modifications.includes('Gradient'),
            rightCornerMark: modifications.includes('RightCornerMark'),
            fit: 'crop',
            w: breakpoint,
            h: (222 / 260) * breakpoint,
            auto: ['format', 'compress'],
          }),
        },
      ],
    },
    {
      maxPixelWidth: 0,
      sourceSet: [
        {
          dpr: 1,
          url: toImgix(standardImage, {
            gradient: modifications.includes('Gradient'),
            rightCornerMark: modifications.includes('RightCornerMark'),
            fit: 'crop',
            w: maxImageWidth,
            h: (490 / 870) * maxImageWidth,
            auto: ['format', 'compress'],
          }),
        },
      ],
    },
  ];

  return {
    url: sources[sources.length - 1].sourceSet[0].url,
    aspectRatio: null,
    sources,
  };
}
